import React, { ReactElement, useState } from 'react'
import { Navigate, matchPath } from 'react-router-dom'
import { getCurrentUser } from 'helpers/auth'
import { useAuth } from 'hooks/auth'
import { PROFILE, SIGN_IN, DOCUMENT, TABLE, SETTINGS, HUB, PROJECT, PROCESS, OLD_TABLE } from 'routes'
import { saveCurrentUrlForRedirect } from 'helpers/redirect'

interface AuthGuardProps {
  children: ReactElement
}

// Decides from `user`, which AuthProvider keeps in step with other tabs. The guard never keeps a copy of
// the stored session or writes one back: the React Compiler caches a storage read made in render for the
// tab's life, and writing that copy back undid every other tab's sign-out.
const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { user } = useAuth()
  // Whether this tab started with a session, to tell "session expired" from "never signed in"
  const [hadSession] = useState(() => !!getCurrentUser())

  // React Router v6: matchPath(pattern, pathname)
  const allowedOptionalAuth =
    !!matchPath(SIGN_IN, window.location.pathname) ||
    !!matchPath(DOCUMENT, window.location.pathname) ||
    !!matchPath(TABLE, window.location.pathname) ||
    !!matchPath(SETTINGS, window.location.pathname) ||
    !!matchPath(PROFILE, window.location.pathname) ||
    !!matchPath(HUB, window.location.pathname) ||
    !!matchPath(PROJECT, window.location.pathname) ||
    !!matchPath(PROCESS, window.location.pathname) ||
    !!matchPath(OLD_TABLE, window.location.pathname)

  if (!user && !allowedOptionalAuth) {
    // Save the current URL so the user can return after signing in
    saveCurrentUrlForRedirect()
    const reason = hadSession ? '?reason=sessionExpired' : ''
    return <Navigate to={`${SIGN_IN}${reason}`} replace />
  }

  return children
}

export default AuthGuard
